import type { FieldErrors, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import { SlugInput } from '@/components/common/SlugInput'
import { workspaceApi } from '@/api'

interface WorkspaceSlugFieldProps {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    register: UseFormRegister<any>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    watch: UseFormWatch<any>
    /** SlugInput pushes its derived value back into form state through this. */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setValue: UseFormSetValue<any>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    errors: FieldErrors<any>
    /** Locks the field. Set on the edit flow: a workspace slug is fixed at creation. */
    slugLocked?: boolean
    /**
     * When provided, the input runs a live availability check against this ClientCompanyId. Omit for
     * create-new-company flows, where the company and its first workspace are created in one request
     * and no collision is possible.
     */
    clientCompanyIdForSlugCheck?: string
}

/**
 * The workspace slug, which is three things that only work together:
 *
 * 1. a visible input that derives its value from the workspace name,
 * 2. a `setValue` write that pushes that derived value into form state, and
 * 3. a hidden registration that carries the validation rules, and is the only reason the slug
 *    counts toward the form being valid at all.
 *
 * Extracted so all three sit in one named place. Inline, the hidden input was easy to read past —
 * and every failure here is silent, because the field looks filled in while the form refuses to
 * submit, or submits without a slug (ORB-570).
 */
export function WorkspaceSlugField({
    register,
    watch,
    setValue,
    errors,
    slugLocked,
    clientCompanyIdForSlugCheck,
}: WorkspaceSlugFieldProps) {
    return (
        <>
            <SlugInput
                label="Slug"
                required
                value={(watch('Slug') as string) || ''}
                onChange={(v) => setValue('Slug', v, { shouldValidate: true, shouldDirty: true })}
                sourceName={watch('Name') as string}
                error={errors.Slug?.message as string | undefined}
                locked={slugLocked}
                lockedMessage="Slug is set at creation and cannot be changed."
                checkAvailability={clientCompanyIdForSlugCheck ? async (slug) => {
                    const res = await workspaceApi.checkSlug(clientCompanyIdForSlugCheck, slug)
                    return res.data ?? { available: false }
                } : undefined}
                helperText="URL segment for deployed landing pages in this workspace."
            />
            {/* Hidden registration so the form tracks slug for submission/validation */}
            <input type="hidden" {...register('Slug', {
                required: 'Slug is required',
                pattern: { value: /^[a-z0-9]+(?:-[a-z0-9]+)*$/, message: 'Slug must be lowercase letters, numbers, and single hyphens.' },
                minLength: { value: 3, message: 'Slug must be at least 3 characters.' },
                maxLength: { value: 50, message: 'Slug must be 50 characters or fewer.' },
            })} />
        </>
    )
}
